import { c as _c } from "react/compiler-runtime";
import { m, AnimatePresence } from 'motion/react';
import Image from 'next/image';
import type { QueueItem, TryOnStage } from '@/types/closet';

// How much of the in-flight item's arc each reported stage fills. Generation
// is the long step, so it starts well short of full.
const STAGE_FILL: Record<TryOnStage, number> = {
  queued: 0.15,
  generating: 0.4,
  cutout: 0.85
};
const STAGE_LABEL: Record<TryOnStage, string> = {
  queued: 'QUEUED',
  generating: 'GENERATING',
  cutout: 'CUTTING OUT'
};
interface CircularQueueIndicatorProps {
  queue: QueueItem[];
  currentProcessingIndex?: number;
}
export function CircularQueueIndicator(t0) {
  const $ = _c(32);
  const {
    queue
  } = t0;
  if (queue.length === 0) {
    return null;
  }
  let t1;
  if ($[0] !== queue) {
    t1 = queue.find(_temp) || queue[queue.length - 1];
    $[0] = queue;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const currentItem = t1;
  const circumference = 2 * Math.PI * 50;
  const segment = circumference / queue.length;
  const gap = queue.length > 1 ? 6 : 0;
  let t2;
  if ($[2] !== queue) {
    t2 = queue.filter(_temp2);
    $[2] = queue;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== queue.length || $[5] !== t2.length) {
    t3 = <p className="text-sm font-medium text-white/90">{t2.length}/{queue.length}</p>;
    $[4] = queue.length;
    $[5] = t2.length;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = currentItem?.status === "processing" && currentItem.stage ? currentItem.stage === "queued" && currentItem.queuePosition !== undefined ? `QUEUED #${currentItem.queuePosition + 1}` : STAGE_LABEL[currentItem.stage] : "ITEMS";
  let t5;
  if ($[7] !== t4) {
    t5 = <p className="text-xs text-white/60">{t4}</p>;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t3 || $[10] !== t5) {
    t6 = <div className="mb-2 text-center">{t3}{t5}</div>;
    $[9] = t3;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      transform: "rotate(-90deg)"
    };
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== gap || $[14] !== queue || $[15] !== segment) {
    let t9;
    if ($[17] !== gap || $[18] !== segment) {
      t9 = (queued, index) => {
        const arc = segment - gap;
        const fill = queued.status === "processing" && queued.stage ? arc * STAGE_FILL[queued.stage] : 0;
        return <g key={`${queued.item.id}-${index}`}><circle cx={64} cy={64} r={50} fill="none" strokeWidth="3" strokeLinecap="round" strokeDasharray={`${arc} ${circumference}`} strokeDashoffset={-index * segment} className={`transition-[stroke] duration-300 ${queued.status === "completed" ? "stroke-white" : queued.status === "processing" ? "animate-pulse stroke-blue-500/40" : "stroke-white/15"}`} />{fill > 0 && <circle cx={64} cy={64} r={50} fill="none" strokeWidth="3" strokeLinecap="round" strokeDasharray={`${fill} ${circumference}`} strokeDashoffset={-index * segment} className="stroke-blue-500 transition-[stroke-dasharray] duration-500 ease-out" />}</g>;
      };
      $[17] = gap;
      $[18] = segment;
      $[19] = t9;
    } else {
      t9 = $[19];
    }
    t8 = queue.map(t9);
    $[13] = gap;
    $[14] = queue;
    $[15] = segment;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[20] !== t8) {
    t9 = <svg width="128" height="128" className="absolute inset-0" style={t7} aria-hidden="true">{t8}</svg>;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== currentItem) {
    t10 = currentItem && <div className="absolute inset-0 flex items-center justify-center"><m.div key={currentItem.item.id} initial={{
        scale: 0.95
      }} animate={{
        scale: 1
      }} exit={{
        scale: 0.95
      }} transition={{
        duration: 0.3
      }} className="relative size-20 overflow-hidden rounded-full border-2 border-white/20 bg-white shadow-lg"><Image src={currentItem.item.imageUrl} alt={currentItem.item.name} fill={true} className="object-cover" sizes="80px" />{currentItem.status === "completed" && <div className="absolute inset-0 flex items-center justify-center bg-green-500/20"><svg className="size-8 text-green-600" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.71 5.29a1 1 0 010 1.41l-8 8a1 1 0 01-1.41 0l-4-4a1 1 0 11.41-1.41L8 12.59l7.29-7.29a1 1 0 11.41 0z" clipRule="evenodd" /></svg></div>}</m.div></div>;
    $[22] = currentItem;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10) {
    t11 = <AnimatePresence mode="wait">{t10}</AnimatePresence>;
    $[24] = t10;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t11 || $[27] !== t9) {
    t12 = <div className="relative size-32">{t9}{t11}</div>;
    $[26] = t11;
    $[27] = t9;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t12 || $[30] !== t6) {
    t13 = <div className="absolute top-4 right-4 z-20">{t6}{t12}</div>;
    $[29] = t12;
    $[30] = t6;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  return t13;
}
function _temp2(q) {
  return q.status === "completed";
}
function _temp(item) {
  return item.status === "processing";
}